import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, Smartphone, Laptop, Tablet, Camera, Gamepad2, Speaker, Guitar, Cpu } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const HighTechPricing = () => {
  const { language } = useLanguage();
  const [selectedItem, setSelectedItem] = useState('');

  const translations = {
    fr: {
      title: 'Tarifications de matériels de haute technologie',
      subtitle: 'Sélectionnez un article pour voir le prix',
      selectItem: 'Sélectionner un article',
      cost: 'Coût',
      additionalInfo: 'Information additionnelle',
      note: 'Remarque: Les prix ci-dessous ont rapport aux expéditions entre Ottawa/Montréal et Kinshasa. Pour les matériels électroniques venant de Kinshasa, les montants à payer sont les mêmes, sauf qu\'ils seront en dollars Américains.'
    },
    en: {
      title: 'High-Tech Equipment Pricing',
      subtitle: 'Select an item to see the price',
      selectItem: 'Select an item',
      cost: 'Cost',
      additionalInfo: 'Additional information',
      note: 'Note: The prices below relate to shipments between Ottawa/Montreal and Kinshasa. For electronic materials coming from Kinshasa, the amounts to pay are the same, except they will be in US dollars.'
    }
  };

  const t = translations[language] || translations.en;

  // High-tech equipment data from the original website
  const highTechItems = [
    {
      id: 'cellphone',
      name: language === 'fr' ? 'Téléphone cellulaire' : 'Cell Phone',
      icon: Smartphone,
      cost: language === 'fr' ? 'Entre 70 $CAD et 180 $CAD plus taxes' : 'Between $70 CAD and $180 CAD plus taxes',
      details: language === 'fr' 
        ? `Les frais d'envoi d'un téléphone d'occasion commence à partir de 70 $CAD plus taxes.

• iPhone XR ou Samsung A15 : 70 $CAD plus taxes
• iPhone 11 : 80 $CAD plus taxes
• iPhone 12 simple : 90 $CAD plus taxes
• iPhone 12 Pro et 12 Pro max : 100 $CAD plus taxes
• iPhone 13 simple : 110 $CAD plus taxes
• iPhone 13 Pro, 13 Pro max et Samsung S22 : 120 $CAD plus taxes
• iPhone 14 simple et Samsung Fold 3 : 130 $CAD plus taxes
• iPhone 14 Pro, 14 Pro max, Samsung S23 et Fold 4 : 140 $CAD plus taxes
• iPhone 15, Samsung S24 et Fold 5 : 150 $CAD plus taxes
• iPhone 16, Samsung S25 et Fold 6 : 160 $CAD plus taxes
• iPhone 17 simple: 170 $CAD plus taxes
• iPhone 17 Pro Max: 180 $CAD plus taxes`
        : `Shipping fees for a used phone start from $70 CAD plus taxes.

• iPhone XR or Samsung A15: $70 CAD plus taxes
• iPhone 11: $80 CAD plus taxes
• iPhone 12 simple: $90 CAD plus taxes
• iPhone 12 Pro and 12 Pro max: $100 CAD plus taxes
• iPhone 13 simple: $110 CAD plus taxes
• iPhone 13 Pro, 13 Pro max and Samsung S22: $120 CAD plus taxes
• iPhone 14 simple and Samsung Fold 3: $130 CAD plus taxes
• iPhone 14 Pro, 14 Pro max, Samsung S23 and Fold 4: $140 CAD plus taxes
• iPhone 15, Samsung S24 and Fold 5: $150 CAD plus taxes
• iPhone 16, Samsung S25 and Fold 6: $160 CAD plus taxes
• iPhone 17 simple: $170 CAD plus taxes
• iPhone 17 Pro Max: $180 CAD plus taxes`
    },
    {
      id: 'camera',
      name: language === 'fr' ? 'Appareil photo' : 'Camera',
      icon: Camera,
      cost: language === 'fr' ? 'Entre 120 $CAD et 180 $CAD plus taxes' : 'Between $120 CAD and $180 CAD plus taxes',
      details: language === 'fr'
        ? "L'envoi d'un appareil photo d'occasion commence à partir de 120 $CAD plus taxes. Un appareil photo neuf est de 180 $CAD plus taxes."
        : "Shipping a used camera starts from $120 CAD plus taxes. A new camera is $180 CAD plus taxes."
    },
    {
      id: 'laptop',
      name: language === 'fr' ? 'Ordinateur portable (laptop)' : 'Laptop Computer',
      icon: Laptop,
      cost: language === 'fr' ? 'Entre 120 $CAD et 170 $CAD plus taxes' : 'Between $120 CAD and $170 CAD plus taxes',
      details: language === 'fr'
        ? "L'envoi d'un ordinateur portable d'occasion commence à partir de 120 $CAD plus taxes. Le prix d'un ordinateur portable neuf est de 170 $CAD plus taxes."
        : "Shipping a used laptop starts from $120 CAD plus taxes. The price for a new laptop is $170 CAD plus taxes."
    },
    {
      id: 'harddrive',
      name: language === 'fr' ? 'Disque dur externe et chargeur portatif' : 'External Hard Drive & Portable Charger',
      icon: Cpu,
      cost: '70 $CAD plus taxes',
      details: language === 'fr'
        ? "Le prix est le même pour un item d'occasion et un neuf."
        : "The price is the same for a used or new item."
    },
    {
      id: 'tensiometer',
      name: language === 'fr' ? 'Tensiomètre' : 'Blood Pressure Monitor',
      icon: Cpu,
      cost: '100 $CAD plus taxes',
      details: language === 'fr'
        ? "Un appareil neuf ou d'occasion coûte 100 $CAD plus taxes."
        : "A new or used device costs $100 CAD plus taxes."
    },
    {
      id: 'tablet',
      name: language === 'fr' ? 'Tablette ou Surface Pro' : 'Tablet or Surface Pro',
      icon: Tablet,
      cost: language === 'fr' ? 'Entre 100 $CAD et 150 $CAD plus taxes' : 'Between $100 CAD and $150 CAD plus taxes',
      details: language === 'fr'
        ? "L'envoi d'une tablette d'occasion commence à partir de 100 $CAD plus taxes. Le prix des tablettes ou laptops Surface neufs est de 150 $CAD plus taxes."
        : "Shipping a used tablet starts from $100 CAD plus taxes. The price for new tablets or Surface laptops is $150 CAD plus taxes."
    },
    {
      id: 'hoverboard',
      name: language === 'fr' ? 'Skate électrique ou gyroskate (Hoverboard)' : 'Electric Skateboard or Hoverboard',
      icon: Gamepad2,
      cost: language === 'fr' ? 'Entre 325 $CAD et 450 $CAD plus taxes' : 'Between $325 CAD and $450 CAD plus taxes',
      details: language === 'fr'
        ? "Les frais d'expédition d'un Hoverboard d'occasion coûte 325 $CAD plus taxes. Les frais d'expédition d'un nouveau Hoverboard est entre 400 $CAD et 450 $CAD plus taxes."
        : "Shipping fees for a used Hoverboard cost $325 CAD plus taxes. Shipping fees for a new Hoverboard is between $400 CAD and $450 CAD plus taxes."
    },
    {
      id: 'drone',
      name: 'Drone',
      icon: Camera,
      cost: language === 'fr' ? 'Entre 100 $CAD et 140 $CAD plus taxes' : 'Between $100 CAD and $140 CAD plus taxes',
      details: language === 'fr'
        ? "L'envoi de drone d'occasion coûte 100 $CAD plus taxes. Le prix de drone neuf est 140 $CAD plus taxes."
        : "Shipping a used drone costs $100 CAD plus taxes. The price for a new drone is $140 CAD plus taxes."
    },
    {
      id: 'nintendo',
      name: 'Nintendo Switch',
      icon: Gamepad2,
      cost: '90 $CAD plus taxes',
      details: language === 'fr'
        ? "Neuf ou d'occasion coûte 90 $CAD plus taxes."
        : "New or used costs $90 CAD plus taxes."
    },
    {
      id: 'playstation',
      name: 'Playstation',
      icon: Gamepad2,
      cost: language === 'fr' ? 'Entre 180 $CAD et 250 $CAD plus taxes' : 'Between $180 CAD and $250 CAD plus taxes',
      details: language === 'fr'
        ? "Les frais d'expédition d'une Playstation 3 et 4 coûte 180 $CAD plus taxes. Pour une Playstation 5, le prix dépend de son état (neuf ou d'occasion); à partir de 190 $CAD plus taxes."
        : "Shipping fees for a Playstation 3 and 4 cost $180 CAD plus taxes. For a Playstation 5, the price depends on its condition (new or used); starting from $190 CAD plus taxes."
    },
    {
      id: 'oculus',
      name: language === 'fr' ? 'Casque de réalité virtuelle (Oculus Quest)' : 'VR Headset (Oculus Quest)',
      icon: Gamepad2,
      cost: '100 $CAD plus taxes',
      details: language === 'fr'
        ? "Neuf ou d'occasion coûte 100 $CAD plus taxes."
        : "New or used costs $100 CAD plus taxes."
    },
    {
      id: 'bluetooth-speaker',
      name: language === 'fr' ? 'Haut-parleur Bluetooth' : 'Bluetooth Speaker',
      icon: Speaker,
      cost: language === 'fr' ? 'Entre 50 $CAD et 120 $CAD plus taxes' : 'Between $50 CAD and $120 CAD plus taxes',
      details: language === 'fr'
        ? "Le prix à payer pour le transport d'un haut-parleur Bluetooth dépend de sa valeur marchande."
        : "The price to pay for transporting a Bluetooth speaker depends on its market value."
    },
    {
      id: 'partybox',
      name: 'Haut-parleur Bluetooth - PartyBox',
      icon: Speaker,
      cost: language === 'fr' ? 'Entre 350 $CAD et 650 $CAD plus taxes' : 'Between $350 CAD and $650 CAD plus taxes',
      details: language === 'fr'
        ? "Le prix à payer pour le transport d'un haut-parleur PartyBox dépend de sa valeur marchande et de son poids."
        : "The price to pay for transporting a PartyBox speaker depends on its market value and weight."
    },
    {
      id: 'amplifier',
      name: language === 'fr' ? 'Amplificateur' : 'Amplifier',
      icon: Speaker,
      cost: language === 'fr' ? 'À partir de 150 $CAD plus taxes' : 'Starting from $150 CAD plus taxes',
      details: language === 'fr'
        ? "Le prix à payer pour le transport d'un amplificateur dépend de sa valeur marchande et de son poids. Les amplificateurs sont de différentes dimensions et de valeur marchandes. Ceci dit, les prix en dépendent aussi."
        : "The price to pay for transporting an amplifier depends on its market value and weight. Amplifiers come in different sizes and market values. That said, the prices depend on these factors as well."
    },
    {
      id: 'metal-detector',
      name: language === 'fr' ? 'Détecteur des métaux' : 'Metal Detector',
      icon: Cpu,
      cost: language === 'fr' ? 'À partir de 190 $CAD plus taxes' : 'Starting from $190 CAD plus taxes',
      details: language === 'fr'
        ? "Le prix à payer pour le transport de détecteur des métaux dépend de la valeur de l'appareil. Les détecteurs des métaux sont de différentes dimensions et de différents modèles. Ceci dit, les prix en dépendent aussi."
        : "The price to pay for transporting a metal detector depends on the value of the device. Metal detectors come in different sizes and models. That said, the prices depend on these factors as well."
    },
    {
      id: 'acoustic-guitar',
      name: language === 'fr' ? 'Guitare acoustique' : 'Acoustic Guitar',
      icon: Guitar,
      cost: '180 $CAD plus taxes',
      details: language === 'fr'
        ? "Le prix est le même pour une guitare d'occasion ou une neuve. Veuillez SVP mettre la guitare dans une housse de transport solide contenant une couche d'éponge pour renforcer la protection."
        : "The price is the same for a used or new guitar. Please put the guitar in a sturdy carrying case with a layer of sponge for extra protection."
    },
    {
      id: 'electric-guitar',
      name: language === 'fr' ? 'Guitare électrique (Basse, rythmique, mi-solo ou solo)' : 'Electric Guitar (Bass, rhythm, mid-solo or solo)',
      icon: Guitar,
      cost: '260 $CAD plus taxes',
      details: language === 'fr'
        ? "Le prix est le même pour une guitare d'occasion ou une neuve. Veuillez SVP mettre la guitare dans une housse de transport solide contenant une couche d'éponge pour renforcer la protection."
        : "The price is the same for a used or new guitar. Please put the guitar in a sturdy carrying case with a layer of sponge for extra protection."
    },
    {
      id: 'synthesizer',
      name: language === 'fr' ? 'Synthétiseur' : 'Synthesizer',
      icon: Guitar,
      cost: language === 'fr' ? 'À partir de 220 $CAD plus taxes' : 'Starting from $220 CAD plus taxes',
      details: language === 'fr'
        ? "Le prix à payer pour le transport d'un Synthétiseur dépend de sa valeur. Les Synthétiseurs sont de différentes dimensions et valeurs. Ceci dit, les prix en dépendent aussi. Veuillez SVP mettre le clavier/synthétiseur dans une housse de transport solide contenant une couche d'éponge pour renforcer la protection."
        : "The price to pay for transporting a Synthesizer depends on its value. Synthesizers come in different sizes and values. That said, the prices depend on these factors as well. Please put the keyboard/synthesizer in a sturdy carrying case with a layer of sponge for extra protection."
    }
  ];

  const selectedItemData = highTechItems.find(item => item.id === selectedItem);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900">{t.title}</h3>
          <p className="text-sm text-slate-500">{t.subtitle}</p>
        </div>
      </div>

      {/* Select dropdown */}
      <Select value={selectedItem} onValueChange={setSelectedItem}>
        <SelectTrigger className="h-12 bg-white">
          <SelectValue placeholder={t.selectItem} />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {highTechItems.map((item) => {
            const Icon = item.icon;
            return (
              <SelectItem key={item.id} value={item.id}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-purple-500" />
                  <span>{item.name}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {/* Selected item details */}
      {selectedItemData && (
        <div className="mt-4 bg-white rounded-xl p-4 border border-purple-100 animate-in fade-in duration-300">
          <div className="flex items-start gap-3 mb-4">
            {(() => {
              const Icon = selectedItemData.icon;
              return (
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-purple-600" />
                </div>
              );
            })()}
            <div>
              <h4 className="font-bold text-slate-900">{selectedItemData.name}</h4>
              <div className="mt-1">
                <span className="text-sm text-slate-500">{t.cost}: </span>
                <span className="font-semibold text-purple-600">{selectedItemData.cost}</span>
              </div>
            </div>
          </div>
          
          <div className="border-t border-purple-100 pt-3">
            <p className="text-sm text-slate-500 mb-1">{t.additionalInfo}:</p>
            <p className="text-sm text-slate-700 whitespace-pre-line">{selectedItemData.details}</p>
          </div>
        </div>
      )}

      {/* Note */}
      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
        <p className="text-xs text-amber-800">{t.note}</p>
      </div>
    </div>
  );
};

export default HighTechPricing;
